// src/lib/api.js

const API_BASE =
  process.env.REACT_APP_API_BASE?.replace(/\/$/, "") || "http://127.0.0.1:8000";

// ----------------------------
// AUTH STORAGE
// ----------------------------
export function saveAuth(data) {
  if (data?.access_token) localStorage.setItem("token", data.access_token);
  if ("refresh_token" in (data || {})) {
    if (data?.refresh_token) localStorage.setItem("refresh_token", data.refresh_token);
    else localStorage.removeItem("refresh_token");
  }
  if ("username" in (data || {})) {
    if (data?.username) localStorage.setItem("username", data.username);
    else localStorage.removeItem("username");
  }
  if ("display_name" in (data || {})) {
    if (data?.display_name) localStorage.setItem("display_name", data.display_name);
    else localStorage.removeItem("display_name");
  }
  if (data?.role) localStorage.setItem("role", data.role);
  if (data?.client) localStorage.setItem("client", data.client);
  if ("approval_modules" in (data || {})) {
    localStorage.setItem("approval_modules", JSON.stringify(Array.isArray(data?.approval_modules) ? data.approval_modules : []));
  }
}

export function clearAuth() {
  localStorage.removeItem("token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("username");
  localStorage.removeItem("display_name");
  localStorage.removeItem("role");
  localStorage.removeItem("client");
  localStorage.removeItem("approval_modules");
  localStorage.removeItem("client_features");
}

export function getToken() {
  return localStorage.getItem("token");
}

export function getRefreshToken() {
  return localStorage.getItem("refresh_token");
}

export function getClientName() {
  return localStorage.getItem("client");
}

export function getDisplayUsername() {
  return localStorage.getItem("username");
}

export function getDisplayName() {
  return localStorage.getItem("display_name");
}

export function getApprovalModules() {
  try {
    const value = JSON.parse(localStorage.getItem("approval_modules") || "[]");
    return Array.isArray(value) ? value.map((item) => String(item || "").trim()).filter(Boolean) : [];
  } catch {
    return [];
  }
}

export function canApproveEmsModule(moduleKey) {
  const role = String(localStorage.getItem("role") || "").trim().toLowerCase();
  if (role === "admin") return true;
  return role === "client_approver" && getApprovalModules().includes(String(moduleKey || "").trim());
}

export const DEFAULT_CLIENT_FEATURES = {
  overview: true,
  upload_data: true,
  documents: true,
  carbon_methodology: true,
  iso_evidence_packs: true,
  ems_readiness: true,
  environmental_context: true,
  environmental_policy: true,
  environmental_aspects: true,
  legal_compliance: true,
  ems_governance_policy: true,
  objectives_targets: true,
  controlled_documents: true,
  risk_opportunities: true,
  training_competence: true,
  operational_controls: true,
  actions_deadlines: true,
  internal_audits: true,
  ems_audit_trail: true,
  non_conformance: true,
  certification_workflow: true,
  management_reviews: true,
  overview_settings: true,
  reminders_escalations: true,
};

function normalizeClientFeatures(value) {
  const out = { ...DEFAULT_CLIENT_FEATURES };
  const src = value && typeof value === "object" ? value : {};
  const legacyGroups = {
    carbon_reporting: ["overview", "upload_data", "documents", "overview_settings", "carbon_methodology"],
    iso_registers: [
      "environmental_context",
      "environmental_policy",
      "environmental_aspects",
      "legal_compliance",
      "risk_opportunities",
      "training_competence",
      "internal_audits",
      "non_conformance",
      "management_reviews",
      "controlled_documents",
      "operational_controls",
    ],
    iso_workflows: [
      "objectives_targets",
      "actions_deadlines",
      "ems_audit_trail",
      "certification_workflow",
      "reminders_escalations",
    ],
    iso_evidence_pack: ["iso_evidence_packs"],
    iso_governance: ["ems_readiness", "ems_governance_policy", "environmental_context", "environmental_policy"],
  };

  Object.entries(legacyGroups).forEach(([legacyKey, mappedKeys]) => {
    if (!(legacyKey in src)) return;
    mappedKeys.forEach((key) => {
      out[key] = Boolean(src[legacyKey]);
    });
  });

  Object.keys(out).forEach((k) => {
    if (k in src) out[k] = Boolean(src[k]);
  });
  return out;
}

export function getCachedClientFeatures() {
  try {
    const raw = localStorage.getItem("client_features");
    if (!raw) return { ...DEFAULT_CLIENT_FEATURES };
    return normalizeClientFeatures(JSON.parse(raw));
  } catch {
    return { ...DEFAULT_CLIENT_FEATURES };
  }
}

export function setCachedClientFeatures(features) {
  const normalized = normalizeClientFeatures(features);
  localStorage.setItem("client_features", JSON.stringify(normalized));
  return normalized;
}

function authHeaders(extra = {}) {
  const token = getToken();
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
}

async function readJsonSafe(res) {
  try {
    const text = await res.text();
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch {
      return text.trim() || null;
    }
  } catch {
    return null;
  }
}

function throwApiError(res, data, fallback = "Request failed") {
  const msg =
    data?.detail ||
    data?.message ||
    (typeof data === "string" ? data : null) ||
    fallback;

  // Helpful for UX: if auth expired, clear tokens so UI can react
  if (res?.status === 401) {
    clearAuth();
  }

  const err = new Error(msg);
  err.status = Number(res?.status || 0);
  throw err;
}

let refreshInFlight = null;

function isAuthEndpoint(url) {
  return /\/login$|\/auth\/refresh$/.test(String(url || ""));
}

function withAuthHeader(options = {}) {
  const token = getToken();
  const headers = { ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  return { ...options, headers };
}

async function tryRefreshAccessToken() {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      clearAuth();
      return false;
    }

    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      const data = await readJsonSafe(res);
      if (!res.ok || !data?.access_token) {
        clearAuth();
        return false;
      }

      saveAuth({
        access_token: data.access_token,
        refresh_token: refreshToken,
        role: data.role || localStorage.getItem("role") || "",
        client: data.client || localStorage.getItem("client") || "",
      });
      return true;
    } catch {
      clearAuth();
      return false;
    }
  })().finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
}

async function fetchWithAuthRetry(url, options = {}) {
  const initial = await fetch(url, options);
  if (initial.status !== 401 || isAuthEndpoint(url)) return initial;

  const refreshed = await tryRefreshAccessToken();
  if (!refreshed) return initial;

  return await fetch(url, withAuthHeader(options));
}

// Generic JSON helper
async function fetchJsonOrThrow(url, options = {}, fallbackErr) {
  const res = await fetchWithAuthRetry(url, options);
  const data = await readJsonSafe(res);
  if (!res.ok) throwApiError(res, data, fallbackErr || "Request failed");
  return data;
}

// Blob helper
async function fetchBlobOrThrow(url, options = {}, fallbackErr) {
  const res = await fetchWithAuthRetry(url, options);
  if (!res.ok) {
    const data = await readJsonSafe(res);
    throwApiError(res, data, fallbackErr || "Request failed");
  }
  return await res.blob();
}

// ----------------------------
// LOGIN
// ----------------------------
export async function loginApi(username, password) {
  const body = new FormData();
  body.append("username", username);
  body.append("password", password);

  const data = await fetchJsonOrThrow(
    `${API_BASE}/login`,
    { method: "POST", body },
    "Login failed"
  );

  saveAuth(data);
  return data;
}

// ----------------------------
// CLIENT: UPLOAD FILE (SUPPORTS HISTORIC DATE)
// ----------------------------
export async function uploadFileApi(file, activityDate) {
  const client = getClientName();
  if (!client) {
    throw new Error("Client name missing in localStorage. Please log in again.");
  }
  if (!file) throw new Error("File is required");

  const body = new FormData();
  body.append("file", file);
  body.append("client", client);

  // Send activity_date for historic charting (YYYY-MM-DD)
  if (activityDate) body.append("activity_date", activityDate);

  const data = await fetchJsonOrThrow(
    `${API_BASE}/upload`,
    { method: "POST", headers: authHeaders(), body },
    "Upload failed"
  );

  // Preserve the saved client dataset and the summary for this individual upload.
  return {
    ...(data?.data || {}),
    upload_result: data?.upload_result || null,
  };
}

export async function uploadEvidenceDocumentApi(file) {
  if (!file) throw new Error("File is required");
  const body = new FormData();
  body.append("file", file);
  return await fetchJsonOrThrow(
    `${API_BASE}/documents/evidence-upload`,
    { method: "POST", headers: authHeaders(), body },
    "Evidence upload failed"
  );
}

// ----------------------------
// CLIENT: FETCH CLIENT DATA
// ----------------------------
export async function fetchClientDataApi() {
  const data = await fetchJsonOrThrow(
    `${API_BASE}/data`,
    { headers: authHeaders() },
    "Failed to fetch client data"
  );
  if (data?.data?.client_features) {
    setCachedClientFeatures(data.data.client_features);
  }
  return data;
}

export async function requestPasswordReset(identifier) {
  return await fetchJsonOrThrow(
    `${API_BASE}/auth/password-reset/request`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ identifier }),
    },
    "Unable to request a password reset"
  );
}

export async function completePasswordReset(token, password) {
  return await fetchJsonOrThrow(
    `${API_BASE}/auth/password-reset/complete`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, password }),
    },
    "Unable to set the password"
  );
}

export async function fetchClientFeaturesApi() {
  const data = await fetchJsonOrThrow(
    `${API_BASE}/client/features`,
    { headers: authHeaders() },
    "Failed to load client feature settings"
  );
  return setCachedClientFeatures(data?.features || {});
}

export async function fetchSustainabilitySettingsApi() {
  return await fetchJsonOrThrow(
    `${API_BASE}/sustainability/settings`,
    { headers: authHeaders() },
    "Failed to load sustainability settings"
  );
}

export async function updateSustainabilitySettingsApi(payload) {
  return await fetchJsonOrThrow(
    `${API_BASE}/sustainability/settings`,
    {
      method: "PUT",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload || {}),
    },
    "Failed to update sustainability settings"
  );
}

// ----------------------------
// CLIENT: UPLOAD HISTORY
// ----------------------------
export async function fetchUploadHistoryApi() {
  const data = await fetchJsonOrThrow(
    `${API_BASE}/uploads/history`,
    { headers: authHeaders() },
    "Failed to load history"
  );
  return data?.history || [];
}

// ----------------------------
// EMS: ENVIRONMENTAL ASPECTS REGISTER
// ----------------------------
export async function fetchAspectsRegisterApi() {
  const data = await fetchJsonOrThrow(
    `${API_BASE}/ems/aspects`,
    { headers: authHeaders() },
    "Failed to load aspects register"
  );
  return data?.items || [];
}

export async function createAspectRegisterItemApi(payload) {
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/aspects`,
    {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload || {}),
    },
    "Failed to create aspect register item"
  );
}

export async function updateAspectRegisterItemApi(itemId, payload) {
  if (!itemId) throw new Error("itemId is required");
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/aspects/${encodeURIComponent(itemId)}`,
    {
      method: "PUT",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload || {}),
    },
    "Failed to update aspect register item"
  );
}

export async function deleteAspectRegisterItemApi(itemId) {
  if (!itemId) throw new Error("itemId is required");
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/aspects/${encodeURIComponent(itemId)}`,
    { method: "DELETE", headers: authHeaders() },
    "Failed to delete aspect register item"
  );
}

// ----------------------------
// EMS: LEGAL COMPLIANCE REGISTER
// ----------------------------
export async function fetchLegalRegisterApi() {
  const data = await fetchJsonOrThrow(
    `${API_BASE}/ems/legal`,
    { headers: authHeaders() },
    "Failed to load legal register"
  );
  return data?.items || [];
}

export async function createLegalRegisterItemApi(payload) {
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/legal`,
    {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload || {}),
    },
    "Failed to create legal register item"
  );
}

export async function updateLegalRegisterItemApi(itemId, payload) {
  if (!itemId) throw new Error("itemId is required");
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/legal/${encodeURIComponent(itemId)}`,
    {
      method: "PUT",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload || {}),
    },
    "Failed to update legal register item"
  );
}

export async function deleteLegalRegisterItemApi(itemId) {
  if (!itemId) throw new Error("itemId is required");
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/legal/${encodeURIComponent(itemId)}`,
    { method: "DELETE", headers: authHeaders() },
    "Failed to delete legal register item"
  );
}

export async function downloadAspectsRegisterCsvApi() {
  return await fetchBlobOrThrow(
    `${API_BASE}/ems/aspects/export.csv`,
    { headers: authHeaders() },
    "Failed to export aspects register"
  );
}

export async function downloadLegalRegisterCsvApi() {
  return await fetchBlobOrThrow(
    `${API_BASE}/ems/legal/export.csv`,
    { headers: authHeaders() },
    "Failed to export legal register"
  );
}

// ----------------------------
// EMS: RISK & OPPORTUNITIES REGISTER
// ----------------------------
export async function fetchRiskRegisterApi() {
  const data = await fetchJsonOrThrow(
    `${API_BASE}/ems/risks`,
    { headers: authHeaders() },
    "Failed to load risk register"
  );
  return data?.items || [];
}

export async function createRiskRegisterItemApi(payload) {
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/risks`,
    {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload || {}),
    },
    "Failed to create risk register item"
  );
}

export async function updateRiskRegisterItemApi(itemId, payload) {
  if (!itemId) throw new Error("itemId is required");
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/risks/${encodeURIComponent(itemId)}`,
    {
      method: "PUT",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload || {}),
    },
    "Failed to update risk register item"
  );
}

export async function deleteRiskRegisterItemApi(itemId) {
  if (!itemId) throw new Error("itemId is required");
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/risks/${encodeURIComponent(itemId)}`,
    { method: "DELETE", headers: authHeaders() },
    "Failed to delete risk register item"
  );
}

export async function downloadRiskRegisterCsvApi() {
  return await fetchBlobOrThrow(
    `${API_BASE}/ems/risks/export.csv`,
    { headers: authHeaders() },
    "Failed to export risk register"
  );
}

// ----------------------------
// EMS: INTERNAL AUDITS REGISTER
// ----------------------------
export async function fetchAuditsRegisterApi() {
  const data = await fetchJsonOrThrow(
    `${API_BASE}/ems/audits`,
    { headers: authHeaders() },
    "Failed to load audits register"
  );
  return data?.items || [];
}

export async function createAuditRegisterItemApi(payload) {
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/audits`,
    {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload || {}),
    },
    "Failed to create audits register item"
  );
}

export async function updateAuditRegisterItemApi(itemId, payload) {
  if (!itemId) throw new Error("itemId is required");
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/audits/${encodeURIComponent(itemId)}`,
    {
      method: "PUT",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload || {}),
    },
    "Failed to update audits register item"
  );
}

export async function deleteAuditRegisterItemApi(itemId) {
  if (!itemId) throw new Error("itemId is required");
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/audits/${encodeURIComponent(itemId)}`,
    { method: "DELETE", headers: authHeaders() },
    "Failed to delete audits register item"
  );
}

export async function downloadAuditsRegisterCsvApi() {
  return await fetchBlobOrThrow(
    `${API_BASE}/ems/audits/export.csv`,
    { headers: authHeaders() },
    "Failed to export audits register"
  );
}

// ----------------------------
// EMS: NON-CONFORMANCE & CORRECTIVE ACTIONS REGISTER
// ----------------------------
export async function fetchNonconformanceRegisterApi() {
  const data = await fetchJsonOrThrow(
    `${API_BASE}/ems/nonconformances`,
    { headers: authHeaders() },
    "Failed to load non-conformance register"
  );
  return data?.items || [];
}

export async function createNonconformanceRegisterItemApi(payload) {
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/nonconformances`,
    {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload || {}),
    },
    "Failed to create non-conformance register item"
  );
}

export async function updateNonconformanceRegisterItemApi(itemId, payload) {
  if (!itemId) throw new Error("itemId is required");
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/nonconformances/${encodeURIComponent(itemId)}`,
    {
      method: "PUT",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload || {}),
    },
    "Failed to update non-conformance register item"
  );
}

export async function deleteNonconformanceRegisterItemApi(itemId) {
  if (!itemId) throw new Error("itemId is required");
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/nonconformances/${encodeURIComponent(itemId)}`,
    { method: "DELETE", headers: authHeaders() },
    "Failed to delete non-conformance register item"
  );
}

export async function downloadNonconformanceRegisterCsvApi() {
  return await fetchBlobOrThrow(
    `${API_BASE}/ems/nonconformances/export.csv`,
    { headers: authHeaders() },
    "Failed to export non-conformance register"
  );
}

// ----------------------------
// EMS: MANAGEMENT REVIEWS REGISTER
// ----------------------------
export async function fetchManagementReviewRegisterApi() {
  const data = await fetchJsonOrThrow(
    `${API_BASE}/ems/management-reviews`,
    { headers: authHeaders() },
    "Failed to load management review register"
  );
  return data?.items || [];
}

export async function createManagementReviewRegisterItemApi(payload) {
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/management-reviews`,
    {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload || {}),
    },
    "Failed to create management review register item"
  );
}

export async function updateManagementReviewRegisterItemApi(itemId, payload) {
  if (!itemId) throw new Error("itemId is required");
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/management-reviews/${encodeURIComponent(itemId)}`,
    {
      method: "PUT",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload || {}),
    },
    "Failed to update management review register item"
  );
}

export async function deleteManagementReviewRegisterItemApi(itemId) {
  if (!itemId) throw new Error("itemId is required");
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/management-reviews/${encodeURIComponent(itemId)}`,
    { method: "DELETE", headers: authHeaders() },
    "Failed to delete management review register item"
  );
}

export async function downloadManagementReviewRegisterCsvApi() {
  return await fetchBlobOrThrow(
    `${API_BASE}/ems/management-reviews/export.csv`,
    { headers: authHeaders() },
    "Failed to export management review register"
  );
}

// ----------------------------
// EMS: CONTROLLED DOCUMENTS / PROCEDURES REGISTER
// ----------------------------
export async function fetchProcedureRegisterApi() {
  const data = await fetchJsonOrThrow(
    `${API_BASE}/ems/procedures`,
    { headers: authHeaders() },
    "Failed to load procedures register"
  );
  return data?.items || [];
}

export async function createProcedureRegisterItemApi(payload) {
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/procedures`,
    {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload || {}),
    },
    "Failed to create procedure register item"
  );
}

export async function updateProcedureRegisterItemApi(itemId, payload) {
  if (!itemId) throw new Error("itemId is required");
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/procedures/${encodeURIComponent(itemId)}`,
    {
      method: "PUT",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload || {}),
    },
    "Failed to update procedure register item"
  );
}

export async function deleteProcedureRegisterItemApi(itemId) {
  if (!itemId) throw new Error("itemId is required");
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/procedures/${encodeURIComponent(itemId)}`,
    { method: "DELETE", headers: authHeaders() },
    "Failed to delete procedure register item"
  );
}

export async function downloadProcedureRegisterCsvApi() {
  return await fetchBlobOrThrow(
    `${API_BASE}/ems/procedures/export.csv`,
    { headers: authHeaders() },
    "Failed to export procedures register"
  );
}

export async function fetchProcedureVersionTimelineApi(itemId) {
  if (!itemId) throw new Error("itemId is required");
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/procedures/${encodeURIComponent(itemId)}/timeline`,
    { headers: authHeaders() },
    "Failed to load procedure version timeline"
  );
}

// ----------------------------
// EMS: OPERATIONAL CONTROLS REGISTER
// ----------------------------
export async function fetchOperationalControlsRegisterApi() {
  const data = await fetchJsonOrThrow(
    `${API_BASE}/ems/operational-controls`,
    { headers: authHeaders() },
    "Failed to load operational controls register"
  );
  return data?.items || [];
}

export async function createOperationalControlsRegisterItemApi(payload) {
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/operational-controls`,
    {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload || {}),
    },
    "Failed to create operational control item"
  );
}

export async function updateOperationalControlsRegisterItemApi(itemId, payload) {
  if (!itemId) throw new Error("itemId is required");
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/operational-controls/${encodeURIComponent(itemId)}`,
    {
      method: "PUT",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload || {}),
    },
    "Failed to update operational control item"
  );
}

export async function deleteOperationalControlsRegisterItemApi(itemId) {
  if (!itemId) throw new Error("itemId is required");
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/operational-controls/${encodeURIComponent(itemId)}`,
    { method: "DELETE", headers: authHeaders() },
    "Failed to delete operational control item"
  );
}

export async function downloadOperationalControlsRegisterCsvApi() {
  return await fetchBlobOrThrow(
    `${API_BASE}/ems/operational-controls/export.csv`,
    { headers: authHeaders() },
    "Failed to export operational controls register"
  );
}

// ----------------------------
// EMS: READINESS SUMMARY
// ----------------------------
export async function fetchEnvironmentalContextApi() {
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/environmental-context`,
    { headers: authHeaders() },
    "Failed to load environmental context"
  );
}

export async function updateEnvironmentalContextApi(payload) {
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/environmental-context`,
    {
      method: "PUT",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload || {}),
    },
    "Failed to update environmental context"
  );
}

export async function approveEnvironmentalContextApi(payload) {
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/environmental-context/approve`,
    {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload || {}),
    },
    "Failed to approve environmental context"
  );
}
export async function fetchEnvironmentalPolicyApi() {
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/environmental-policy`,
    { headers: authHeaders() },
    "Failed to load environmental policy"
  );
}

export async function updateEnvironmentalPolicyApi(payload) {
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/environmental-policy`,
    {
      method: "PUT",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload || {}),
    },
    "Failed to update environmental policy"
  );
}

export async function approveEnvironmentalPolicyApi(payload) {
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/environmental-policy/approve`,
    {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload || {}),
    },
    "Failed to approve environmental policy"
  );
}

export async function fetchCarbonMethodologyApi() {
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/carbon-methodology`,
    { headers: authHeaders() },
    "Failed to load carbon methodology"
  );
}

export async function updateCarbonMethodologyApi(payload) {
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/carbon-methodology`,
    {
      method: "PUT",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload || {}),
    },
    "Failed to update carbon methodology"
  );
}

export async function approveCarbonMethodologyApi(payload) {
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/carbon-methodology/approve`,
    {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload || {}),
    },
    "Failed to approve carbon methodology"
  );
}

export async function fetchObjectivesTargetsRegisterApi() {
  const data = await fetchJsonOrThrow(
    `${API_BASE}/ems/objectives-targets`,
    { headers: authHeaders() },
    "Failed to load objectives and targets register"
  );
  return data?.items || [];
}

export async function createObjectivesTargetsRegisterItemApi(payload) {
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/objectives-targets`,
    {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload || {}),
    },
    "Failed to create objective"
  );
}

export async function updateObjectivesTargetsRegisterItemApi(itemId, payload) {
  if (!itemId) throw new Error("itemId is required");
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/objectives-targets/${encodeURIComponent(itemId)}`,
    {
      method: "PUT",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload || {}),
    },
    "Failed to update objective"
  );
}

export async function deleteObjectivesTargetsRegisterItemApi(itemId) {
  if (!itemId) throw new Error("itemId is required");
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/objectives-targets/${encodeURIComponent(itemId)}`,
    { method: "DELETE", headers: authHeaders() },
    "Failed to delete objective"
  );
}

export async function downloadObjectivesTargetsRegisterCsvApi() {
  return await fetchBlobOrThrow(
    `${API_BASE}/ems/objectives-targets/export.csv`,
    { headers: authHeaders() },
    "Failed to export objectives and targets register"
  );
}

export async function fetchTrainingCompetenceRegisterApi() {
  const data = await fetchJsonOrThrow(
    `${API_BASE}/ems/training-competence`,
    { headers: authHeaders() },
    "Failed to load training and competence register"
  );
  return data?.items || [];
}

export async function createTrainingCompetenceRegisterItemApi(payload) {
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/training-competence`,
    {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload || {}),
    },
    "Failed to create training record"
  );
}

export async function updateTrainingCompetenceRegisterItemApi(itemId, payload) {
  if (!itemId) throw new Error("itemId is required");
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/training-competence/${encodeURIComponent(itemId)}`,
    {
      method: "PUT",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload || {}),
    },
    "Failed to update training record"
  );
}

export async function deleteTrainingCompetenceRegisterItemApi(itemId) {
  if (!itemId) throw new Error("itemId is required");
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/training-competence/${encodeURIComponent(itemId)}`,
    { method: "DELETE", headers: authHeaders() },
    "Failed to delete training record"
  );
}

export async function downloadTrainingCompetenceRegisterCsvApi() {
  return await fetchBlobOrThrow(
    `${API_BASE}/ems/training-competence/export.csv`,
    { headers: authHeaders() },
    "Failed to export training and competence register"
  );
}

export async function fetchEmsReadinessApi() {
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/readiness`,
    { headers: authHeaders() },
    "Failed to load EMS readiness summary"
  );
}

export async function fetchEmsGovernancePolicyApi() {
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/governance-policy`,
    { headers: authHeaders() },
    "Failed to load EMS governance policy"
  );
}

export async function updateEmsGovernancePolicyApi(payload) {
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/governance-policy`,
    {
      method: "PUT",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload || {}),
    },
    "Failed to update EMS governance policy"
  );
}

export async function fetchEmsStageReadinessApi() {
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/stage-readiness`,
    { headers: authHeaders() },
    "Failed to load stage readiness"
  );
}

export async function updateEmsStageReadinessApi(payload) {
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/stage-readiness`,
    {
      method: "PUT",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload || {}),
    },
    "Failed to update stage readiness"
  );
}

export async function signoffEmsStageReadinessApi(payload) {
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/stage-readiness/signoff`,
    {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload || {}),
    },
    "Failed to submit stage sign-off"
  );
}

export async function fetchEmsActionsApi() {
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/actions`,
    { headers: authHeaders() },
    "Failed to load EMS actions"
  );
}

export async function downloadEmsActionsCsvApi() {
  return await fetchBlobOrThrow(
    `${API_BASE}/ems/actions/export.csv`,
    { headers: authHeaders() },
    "Failed to export EMS actions"
  );
}

export async function requestEmsApprovalApi(payload) {
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/approvals/request`,
    {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload || {}),
    },
    "Failed to request approval"
  );
}

export async function decideEmsApprovalApi(payload) {
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/approvals/decision`,
    {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload || {}),
    },
    "Failed to submit approval decision"
  );
}

export async function verifyEmsClosureApi(payload) {
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/approvals/verify-close`,
    {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload || {}),
    },
    "Failed to verify closure"
  );
}

export async function fetchEmsAuditLogApi(filters = {}) {
  const params = new URLSearchParams();
  if (filters?.module_key) params.set("module_key", filters.module_key);
  if (filters?.action) params.set("action", filters.action);
  if (filters?.actor) params.set("actor", filters.actor);
  if (filters?.date_from) params.set("date_from", filters.date_from);
  if (filters?.date_to) params.set("date_to", filters.date_to);
  const qs = params.toString();

  const data = await fetchJsonOrThrow(
    `${API_BASE}/ems/audit-log${qs ? `?${qs}` : ""}`,
    { headers: authHeaders() },
    "Failed to load EMS audit log"
  );
  return data?.entries || [];
}

export async function downloadEmsAuditLogCsvApi(filters = {}) {
  const params = new URLSearchParams();
  if (filters?.module_key) params.set("module_key", filters.module_key);
  if (filters?.action) params.set("action", filters.action);
  if (filters?.actor) params.set("actor", filters.actor);
  if (filters?.date_from) params.set("date_from", filters.date_from);
  if (filters?.date_to) params.set("date_to", filters.date_to);
  const qs = params.toString();

  return await fetchBlobOrThrow(
    `${API_BASE}/ems/audit-log/export.csv${qs ? `?${qs}` : ""}`,
    { headers: authHeaders() },
    "Failed to export EMS audit log"
  );
}

export async function fetchEmsApprovalPolicyApi() {
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/approval-policy`,
    { headers: authHeaders() },
    "Failed to load EMS approval policy"
  );
}

export async function downloadIsoEvidencePackExcelApi() {
  return await fetchBlobOrThrow(
    `${API_BASE}/ems/evidence-pack.xlsx`,
    { headers: authHeaders() },
    "Failed to export ISO evidence pack (Excel)"
  );
}

export async function downloadIsoEvidencePackPdfApi() {
  return await fetchBlobOrThrow(
    `${API_BASE}/ems/evidence-pack.pdf`,
    { headers: authHeaders() },
    "Failed to export ISO evidence pack (PDF)"
  );
}

export async function fetchEmsRemindersApi(daysAhead = 14) {
  const n = Number(daysAhead);
  const safe = Number.isFinite(n) ? Math.min(90, Math.max(1, Math.floor(n))) : 14;
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/reminders?days_ahead=${encodeURIComponent(safe)}`,
    { headers: authHeaders() },
    "Failed to load EMS reminders"
  );
}

export async function downloadEmsRemindersCsvApi(daysAhead = 14) {
  const n = Number(daysAhead);
  const safe = Number.isFinite(n) ? Math.min(90, Math.max(1, Math.floor(n))) : 14;
  return await fetchBlobOrThrow(
    `${API_BASE}/ems/reminders/export.csv?days_ahead=${encodeURIComponent(safe)}`,
    { headers: authHeaders() },
    "Failed to export EMS reminders"
  );
}

export async function sendEmsRemindersEmailApi(payload) {
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/reminders/send`,
    {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload || {}),
    },
    "Failed to send reminder emails"
  );
}

export async function sendEmsTestEmailApi(payload) {
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/reminders/test-email`,
    {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload || {}),
    },
    "Failed to send test email"
  );
}

export async function adminDispatchEmsRemindersApi(payload) {
  return await fetchJsonOrThrow(
    `${API_BASE}/admin/ems/reminders/dispatch`,
    {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload || {}),
    },
    "Failed to dispatch reminders"
  );
}

// ----------------------------
// CLIENT: MANUAL ENTRY (JSON)
// ----------------------------
export async function manualEntryApi(payload) {
  return await fetchJsonOrThrow(
    `${API_BASE}/manual-entry`,
    {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload),
    },
    "Manual entry failed"
  );
}

// ----------------------------
// CLIENT: DELETE PARSED ENTRY
// ----------------------------
export async function deleteEntryApi(entryId) {
  if (!entryId) throw new Error("entryId is required");

  return await fetchJsonOrThrow(
    `${API_BASE}/entries/${encodeURIComponent(entryId)}`,
    { method: "DELETE", headers: authHeaders() },
    "Failed to delete entry"
  );
}

// ----------------------------
// CLIENT: PDF REPORT DOWNLOAD (existing / old)
// ----------------------------
export async function downloadPdfReportApi() {
  return await fetchBlobOrThrow(
    `${API_BASE}/report/pdf`,
    { headers: authHeaders() },
    "Failed to download report"
  );
}

// ----------------------------
// ✅ CLIENT: PDF REPORT WITH DASHBOARD CHARTS (NEW)
// Backend endpoint you’ll add: POST /report/pdf-dashboard
// ----------------------------

// POST (dashboard charts + timeframe)
export async function downloadPdfDashboardReportApi(payload) {
  return await fetchBlobOrThrow(
    `${API_BASE}/report/pdf`,
    {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload),
    },
    "Failed to generate dashboard PDF"
  );
}

// ----------------------------
// CLIENT: EXCEL BREAKDOWN DOWNLOAD
// ----------------------------
export async function downloadBreakdownExcelApi() {
  return await fetchBlobOrThrow(
    `${API_BASE}/report/breakdown.xlsx`,
    { headers: authHeaders() },
    "Failed to download breakdown spreadsheet"
  );
}

// ----------------------------
// DEFRA OPTIONS (for manual entry dropdowns)
// ----------------------------
export async function fetchDefraOptionsApi(scope) {
  // scope not currently used by backend (kept for future)
  return await fetchJsonOrThrow(
    `${API_BASE}/defra/options`,
    { headers: authHeaders() },
    "Failed to load DEFRA options"
  );
}

// ----------------------------
// DOCUMENTS
// ----------------------------
export async function fetchDocumentsApi() {
  const data = await fetchJsonOrThrow(
    `${API_BASE}/documents`,
    { headers: authHeaders() },
    "Failed to load documents"
  );
  return data?.documents || [];
}

export async function downloadDocumentApi(docId) {
  if (!docId) throw new Error("docId is required");
  return await fetchBlobOrThrow(
    `${API_BASE}/documents/file/${encodeURIComponent(docId)}`,
    { headers: authHeaders() },
    "Failed to download document"
  );
}

export async function downloadDocumentsZipApi(year) {
  if (!year) throw new Error("year is required");
  return await fetchBlobOrThrow(
    `${API_BASE}/documents/zip?year=${encodeURIComponent(year)}`,
    { headers: authHeaders() },
    "Failed to download documents ZIP"
  );
}

// ----------------------------
// ADMIN
// ----------------------------
export async function fetchAdminStats() {
  return await fetchJsonOrThrow(
    `${API_BASE}/admin/stats`,
    { headers: authHeaders() },
    "Failed to load admin stats"
  );
}

export async function fetchAdminClients() {
  const data = await fetchJsonOrThrow(
    `${API_BASE}/admin/clients`,
    { headers: authHeaders() },
    "Failed to load admin clients"
  );
  return data?.clients || [];
}

export async function fetchAdminDefraStatus() {
  return await fetchJsonOrThrow(
    `${API_BASE}/admin/defra/status`,
    { headers: authHeaders() },
    "Failed to load DEFRA file status"
  );
}

export async function downloadAdminDefraWorkbookApi() {
  return await fetchBlobOrThrow(
    `${API_BASE}/admin/defra/download`,
    { headers: authHeaders() },
    "Failed to download DEFRA workbook"
  );
}

export async function uploadAdminDefraWorkbookApi(file) {
  if (!file) throw new Error("DEFRA workbook is required");
  const body = new FormData();
  body.append("file", file);

  return await fetchJsonOrThrow(
    `${API_BASE}/admin/defra/upload`,
    { method: "POST", headers: authHeaders(), body },
    "Failed to replace DEFRA workbook"
  );
}

export async function createAdminClient(username, password, clientFolder, displayName, email = "") {
  const body = new FormData();
  body.append("username", username);
  body.append("password", password);
  body.append("client_folder", clientFolder);
  body.append("display_name", displayName || "");
  body.append("email", email || "");

  return await fetchJsonOrThrow(
    `${API_BASE}/admin/create-client`,
    { method: "POST", headers: authHeaders(), body },
    "Failed to create client"
  );
}

export async function adminDeleteClient(clientName) {
  if (!clientName) throw new Error("Client name is required");

  return await fetchJsonOrThrow(
    `${API_BASE}/admin/clients/${encodeURIComponent(clientName)}`,
    { method: "DELETE", headers: authHeaders() },
    "Failed to delete client"
  );
}

export async function fetchAdminUsers() {
  const data = await fetchJsonOrThrow(
    `${API_BASE}/admin/users`,
    { headers: authHeaders() },
    "Failed to load admin users"
  );
  return data?.users || [];
}

export async function fetchAdminClientApprovers(clientName) {
  if (!clientName) throw new Error("Client name is required");
  return await fetchJsonOrThrow(
    `${API_BASE}/admin/clients/${encodeURIComponent(clientName)}/approvers`,
    { headers: authHeaders() },
    "Failed to load client approvers"
  );
}

export async function createAdminClientApprover(clientName, payload) {
  if (!clientName) throw new Error("Client name is required");
  return await fetchJsonOrThrow(
    `${API_BASE}/admin/clients/${encodeURIComponent(clientName)}/approvers`,
    {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload || {}),
    },
    "Failed to create client approver"
  );
}

export async function updateAdminClientApprover(clientName, username, payload) {
  if (!clientName || !username) throw new Error("Client name and username are required");
  return await fetchJsonOrThrow(
    `${API_BASE}/admin/clients/${encodeURIComponent(clientName)}/approvers/${encodeURIComponent(username)}`,
    {
      method: "PUT",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload || {}),
    },
    "Failed to update client approver"
  );
}

export async function deleteAdminClientApprover(clientName, username) {
  if (!clientName || !username) throw new Error("Client name and username are required");
  return await fetchJsonOrThrow(
    `${API_BASE}/admin/clients/${encodeURIComponent(clientName)}/approvers/${encodeURIComponent(username)}`,
    { method: "DELETE", headers: authHeaders() },
    "Failed to remove client approver"
  );
}

export async function sendAdminClientAccountInvitation(clientName, username) {
  if (!clientName || !username) throw new Error("Client name and username are required");
  return await fetchJsonOrThrow(
    `${API_BASE}/admin/clients/${encodeURIComponent(clientName)}/accounts/${encodeURIComponent(username)}/invite`,
    { method: "POST", headers: authHeaders() },
    "Failed to send invitation email"
  );
}

export async function fetchAdminClientEmsOwners(clientName) {
  if (!clientName) throw new Error("Client name is required");
  return await fetchJsonOrThrow(
    `${API_BASE}/admin/clients/${encodeURIComponent(clientName)}/ems-owners`,
    { headers: authHeaders() },
    "Failed to load Client EMS Owners"
  );
}

export async function createAdminClientEmsOwner(clientName, payload) {
  if (!clientName) throw new Error("Client name is required");
  return await fetchJsonOrThrow(
    `${API_BASE}/admin/clients/${encodeURIComponent(clientName)}/ems-owners`,
    {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload || {}),
    },
    "Failed to create Client EMS Owner"
  );
}

export async function updateAdminClientEmsOwner(clientName, username, payload) {
  if (!clientName || !username) throw new Error("Client name and username are required");
  return await fetchJsonOrThrow(
    `${API_BASE}/admin/clients/${encodeURIComponent(clientName)}/ems-owners/${encodeURIComponent(username)}`,
    {
      method: "PUT",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload || {}),
    },
    "Failed to update Client EMS Owner"
  );
}

export async function deleteAdminClientEmsOwner(clientName, username) {
  if (!clientName || !username) throw new Error("Client name and username are required");
  return await fetchJsonOrThrow(
    `${API_BASE}/admin/clients/${encodeURIComponent(clientName)}/ems-owners/${encodeURIComponent(username)}`,
    { method: "DELETE", headers: authHeaders() },
    "Failed to remove Client EMS Owner"
  );
}

export async function fetchClientTeamApprovers() {
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/team/approvers`,
    { headers: authHeaders() },
    "Failed to load client approvers"
  );
}

export async function createClientTeamApprover(payload) {
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/team/approvers`,
    {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload || {}),
    },
    "Failed to create client approver"
  );
}

export async function updateClientTeamApprover(username, payload) {
  if (!username) throw new Error("Username is required");
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/team/approvers/${encodeURIComponent(username)}`,
    {
      method: "PUT",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload || {}),
    },
    "Failed to update client approver"
  );
}

export async function deleteClientTeamApprover(username) {
  if (!username) throw new Error("Username is required");
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/team/approvers/${encodeURIComponent(username)}`,
    { method: "DELETE", headers: authHeaders() },
    "Failed to remove client approver"
  );
}

export async function sendClientTeamApproverInvitation(username) {
  if (!username) throw new Error("Username is required");
  return await fetchJsonOrThrow(
    `${API_BASE}/ems/team/approvers/${encodeURIComponent(username)}/invite`,
    { method: "POST", headers: authHeaders() },
    "Failed to send invitation email"
  );
}

export async function updateAdminUserOpsPermissions(username, opsPermissions) {
  if (!username) throw new Error("Username is required");
  return await fetchJsonOrThrow(
    `${API_BASE}/admin/users/${encodeURIComponent(username)}/ops-permissions`,
    {
      method: "PUT",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ ops_permissions: Array.isArray(opsPermissions) ? opsPermissions : [] }),
    },
    "Failed to update ops permissions"
  );
}

export async function adminUpdateClient(clientName, payload) {
  if (!clientName) throw new Error("Client name is required");

  return await fetchJsonOrThrow(
    `${API_BASE}/admin/clients/${encodeURIComponent(clientName)}`,
    {
      method: "PUT",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload || {}),
    },
    "Failed to update client"
  );
}

export async function adminImpersonate(targetClient) {
  const body = new FormData();
  body.append("target_client", targetClient);

  const data = await fetchJsonOrThrow(
    `${API_BASE}/admin/impersonate`,
    { method: "POST", headers: authHeaders(), body },
    "Failed to impersonate client"
  );

  saveAuth({
    access_token: data.access_token,
    refresh_token: "",
    role: data.role,
    client: data.client,
  });

  return data;
}

export async function adminResetPassword(targetClient, newPassword) {
  const body = new FormData();
  body.append("target_client", targetClient);
  body.append("new_password", newPassword);

  return await fetchJsonOrThrow(
    `${API_BASE}/admin/reset-password`,
    { method: "POST", headers: authHeaders(), body },
    "Failed to reset password"
  );
}

export async function downloadAdminReportApi() {
  return await fetchBlobOrThrow(
    `${API_BASE}/admin/report`,
    { headers: authHeaders() },
    "Failed to download admin report"
  );
}

export async function fetchAdminOpsAlertsApi({ status = "", severity = "", limit = 200 } = {}) {
  const qs = new URLSearchParams();
  if (status) qs.set("status", status);
  if (severity) qs.set("severity", severity);
  if (typeof limit === "number" && Number.isFinite(limit) && limit > 0) qs.set("limit", String(limit));
  else if (limit) qs.set("limit", String(limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";

  return await fetchJsonOrThrow(
    `${API_BASE}/admin/ops/alerts${suffix}`,
    { headers: authHeaders() },
    "Failed to load ops alerts"
  );
}

export async function fetchAdminOpsAlertsFilteredApi({
  status = "",
  severity = "",
  acknowledged = "",
  response_status = "",
  limit = 200,
} = {}) {
  const qs = new URLSearchParams();
  if (status) qs.set("status", status);
  if (severity) qs.set("severity", severity);
  if (acknowledged !== "") qs.set("acknowledged", String(acknowledged));
  if (response_status) qs.set("response_status", String(response_status));
  if (limit) qs.set("limit", String(limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";

  return await fetchJsonOrThrow(
    `${API_BASE}/admin/ops/alerts${suffix}`,
    { headers: authHeaders() },
    "Failed to load ops alerts"
  );
}

export async function downloadAdminOpsAlertsCsvApi({ status = "", severity = "" } = {}) {
  const qs = new URLSearchParams();
  if (status) qs.set("status", status);
  if (severity) qs.set("severity", severity);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";

  return await fetchBlobOrThrow(
    `${API_BASE}/admin/ops/alerts/export.csv${suffix}`,
    { headers: authHeaders() },
    "Failed to export ops alerts"
  );
}

export async function downloadAdminOpsAlertsCsvFilteredApi({
  status = "",
  severity = "",
  acknowledged = "",
  response_status = "",
} = {}) {
  const qs = new URLSearchParams();
  if (status) qs.set("status", status);
  if (severity) qs.set("severity", severity);
  if (acknowledged !== "") qs.set("acknowledged", String(acknowledged));
  if (response_status) qs.set("response_status", String(response_status));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return await fetchBlobOrThrow(
    `${API_BASE}/admin/ops/alerts/export.csv${suffix}`,
    { headers: authHeaders() },
    "Failed to export ops alerts"
  );
}

export async function setAdminOpsAlertAckApi(
  alertId,
  { acknowledged = true, note = "", triage_comment = undefined, change_reason = undefined, expected_version = undefined } = {}
) {
  if (!alertId) throw new Error("alertId is required");
  const body = { acknowledged: !!acknowledged, note: String(note || "") };
  if (triage_comment !== undefined) body.triage_comment = String(triage_comment || "");
  if (change_reason !== undefined) body.change_reason = String(change_reason || "");
  if (expected_version !== undefined) body.expected_version = Number(expected_version);
  return await fetchJsonOrThrow(
    `${API_BASE}/admin/ops/alerts/${encodeURIComponent(alertId)}/ack`,
    {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(body),
    },
    "Failed to update alert acknowledgment"
  );
}

export async function setAdminOpsAlertsBulkAckApi({
  alert_ids = [],
  acknowledged = true,
  note = "",
  triage_comment = undefined,
  change_reason = undefined,
  expected_versions = undefined,
} = {}) {
  const ids = Array.isArray(alert_ids) ? alert_ids.map((x) => String(x || "").trim()).filter(Boolean) : [];
  if (!ids.length) throw new Error("alert_ids is required");
  const body = { alert_ids: ids, acknowledged: !!acknowledged, note: String(note || "") };
  if (triage_comment !== undefined) body.triage_comment = String(triage_comment || "");
  if (change_reason !== undefined) body.change_reason = String(change_reason || "");
  if (expected_versions !== undefined) body.expected_versions = expected_versions;
  return await fetchJsonOrThrow(
    `${API_BASE}/admin/ops/alerts/ack/bulk`,
    {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(body),
    },
    "Failed to bulk update alert acknowledgment"
  );
}

export async function setAdminOpsAlertOwnershipApi(
  alertId,
  payload = {}
) {
  if (!alertId) throw new Error("alertId is required");
  const body = {};
  if (Object.prototype.hasOwnProperty.call(payload, "owner")) body.owner = payload.owner;
  if (Object.prototype.hasOwnProperty.call(payload, "sla_due_at")) body.sla_due_at = payload.sla_due_at;
  if (Object.prototype.hasOwnProperty.call(payload, "response_status")) body.response_status = payload.response_status;
  if (Object.prototype.hasOwnProperty.call(payload, "resolution_note")) body.resolution_note = payload.resolution_note;
  if (Object.prototype.hasOwnProperty.call(payload, "triage_comment")) body.triage_comment = payload.triage_comment;
  if (Object.prototype.hasOwnProperty.call(payload, "change_reason")) body.change_reason = payload.change_reason;
  if (Object.prototype.hasOwnProperty.call(payload, "expected_version")) body.expected_version = payload.expected_version;
  return await fetchJsonOrThrow(
    `${API_BASE}/admin/ops/alerts/${encodeURIComponent(alertId)}/ownership`,
    {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(body),
    },
    "Failed to update alert ownership/SLA"
  );
}

export async function setAdminOpsAlertsBulkOwnershipApi({ alert_ids = [], ...payload } = {}) {
  const ids = Array.isArray(alert_ids) ? alert_ids.map((x) => String(x || "").trim()).filter(Boolean) : [];
  if (!ids.length) throw new Error("alert_ids is required");
  const body = { alert_ids: ids };
  if (Object.prototype.hasOwnProperty.call(payload, "owner")) body.owner = payload.owner;
  if (Object.prototype.hasOwnProperty.call(payload, "sla_due_at")) body.sla_due_at = payload.sla_due_at;
  if (Object.prototype.hasOwnProperty.call(payload, "response_status")) body.response_status = payload.response_status;
  if (Object.prototype.hasOwnProperty.call(payload, "resolution_note")) body.resolution_note = payload.resolution_note;
  if (Object.prototype.hasOwnProperty.call(payload, "triage_comment")) body.triage_comment = payload.triage_comment;
  if (Object.prototype.hasOwnProperty.call(payload, "change_reason")) body.change_reason = payload.change_reason;
  if (Object.prototype.hasOwnProperty.call(payload, "expected_versions")) body.expected_versions = payload.expected_versions;
  return await fetchJsonOrThrow(
    `${API_BASE}/admin/ops/alerts/ownership/bulk`,
    {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(body),
    },
    "Failed to bulk update alert ownership/SLA"
  );
}

export async function fetchAdminOpsAlertsSummaryApi({
  status = "",
  severity = "",
  acknowledged = "",
  response_status = "",
} = {}) {
  const qs = new URLSearchParams();
  if (status) qs.set("status", status);
  if (severity) qs.set("severity", severity);
  if (acknowledged !== "") qs.set("acknowledged", String(acknowledged));
  if (response_status) qs.set("response_status", String(response_status));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";

  return await fetchJsonOrThrow(
    `${API_BASE}/admin/ops/alerts/summary${suffix}`,
    { headers: authHeaders() },
    "Failed to load ops alerts summary"
  );
}

export async function sendAdminOpsIncidentTestApi() {
  return await fetchJsonOrThrow(
    `${API_BASE}/admin/ops/incidents/test`,
    { method: "POST", headers: authHeaders() },
    "Failed to send incident test"
  );
}

export async function dispatchAdminOpsSelectedIncidentsApi({
  alert_ids = [],
  routing_key = "",
  send_only_open = true,
  dry_run = false,
} = {}) {
  const ids = Array.isArray(alert_ids) ? alert_ids.map((x) => String(x || "").trim()).filter(Boolean) : [];
  if (!ids.length) throw new Error("alert_ids is required");
  return await fetchJsonOrThrow(
    `${API_BASE}/admin/ops/alerts/incident-dispatch`,
    {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({
        alert_ids: ids,
        routing_key: String(routing_key || ""),
        send_only_open: !!send_only_open,
        dry_run: !!dry_run,
      }),
    },
    "Failed to dispatch selected incidents"
  );
}

export async function dispatchAdminOpsSlaAlertsApi({
  send_email = true,
  send_incident_webhook = true,
  include_due_soon = true,
} = {}) {
  return await fetchJsonOrThrow(
    `${API_BASE}/admin/ops/alerts/sla-dispatch`,
    {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({
        send_email: !!send_email,
        send_incident_webhook: !!send_incident_webhook,
        include_due_soon: !!include_due_soon,
      }),
    },
    "Failed to dispatch SLA alerts"
  );
}

export async function fetchAdminOpsAlertsAuditLogApi({
  action = "",
  actor = "",
  alert_id = "",
  limit = 100,
} = {}) {
  const qs = new URLSearchParams();
  if (action) qs.set("action", String(action));
  if (actor) qs.set("actor", String(actor));
  if (alert_id) qs.set("alert_id", String(alert_id));
  if (limit) qs.set("limit", String(limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return await fetchJsonOrThrow(
    `${API_BASE}/admin/ops/alerts/audit-log${suffix}`,
    { headers: authHeaders() },
    "Failed to load ops alerts audit log"
  );
}

export async function downloadAdminOpsAlertsAuditLogCsvApi({
  action = "",
  actor = "",
  alert_id = "",
} = {}) {
  const qs = new URLSearchParams();
  if (action) qs.set("action", String(action));
  if (actor) qs.set("actor", String(actor));
  if (alert_id) qs.set("alert_id", String(alert_id));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return await fetchBlobOrThrow(
    `${API_BASE}/admin/ops/alerts/audit-log/export.csv${suffix}`,
    { headers: authHeaders() },
    "Failed to export ops alerts audit log"
  );
}

export async function fetchAdminOpsAlertsCorrelationsApi({ limit = 100 } = {}) {
  const qs = new URLSearchParams();
  if (limit) qs.set("limit", String(limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return await fetchJsonOrThrow(
    `${API_BASE}/admin/ops/alerts/correlations${suffix}`,
    { headers: authHeaders() },
    "Failed to load ops alert correlations"
  );
}

export async function fetchAdminOpsAlertsAnalyticsApi({ days = 30 } = {}) {
  const qs = new URLSearchParams();
  if (days) qs.set("days", String(days));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return await fetchJsonOrThrow(
    `${API_BASE}/admin/ops/alerts/analytics${suffix}`,
    { headers: authHeaders() },
    "Failed to load ops alert analytics"
  );
}

export async function escalateAdminOpsAcksApi({
  send_incident_webhook = true,
  threshold_hours = undefined,
  dry_run = false,
} = {}) {
  const body = {
    send_incident_webhook: !!send_incident_webhook,
    dry_run: !!dry_run,
  };
  if (threshold_hours !== undefined) body.threshold_hours = Number(threshold_hours);
  return await fetchJsonOrThrow(
    `${API_BASE}/admin/ops/alerts/escalate-acks`,
    {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(body),
    },
    "Failed to escalate stale acknowledgements"
  );
}

export async function sendAdminOpsDigestApi({ recipients = undefined, days = 7, include_audit_tail = 20 } = {}) {
  const body = { days, include_audit_tail };
  if (Array.isArray(recipients)) body.recipients = recipients;
  return await fetchJsonOrThrow(
    `${API_BASE}/admin/ops/alerts/digest/send`,
    {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(body),
    },
    "Failed to send ops digest"
  );
}

export async function dispatchAdminOpsDigestApi({ force = false } = {}) {
  return await fetchJsonOrThrow(
    `${API_BASE}/admin/ops/alerts/digest/dispatch`,
    {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ force: !!force }),
    },
    "Failed to dispatch ops digest"
  );
}

export async function fetchAdminOpsSlaPolicyApi() {
  return await fetchJsonOrThrow(
    `${API_BASE}/admin/ops/sla-policy`,
    { headers: authHeaders() },
    "Failed to load ops SLA policy"
  );
}

export async function updateAdminOpsSlaPolicyApi(payload) {
  return await fetchJsonOrThrow(
    `${API_BASE}/admin/ops/sla-policy`,
    {
      method: "PUT",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(payload || {}),
    },
    "Failed to update ops SLA policy"
  );
}
